import type { ProductFilters } from '@/types/api';

/**
 * Query-key factory — keep ALL query keys here so cache invalidations are
 * explicit and refactor-safe. Always use `as const` so the keys are
 * comparable by reference (TanStack Query requires structural equality).
 */
export const queryKeys = {
  products: {
    all: ['products'] as const,
    list: (filters: ProductFilters) => ['products', 'list', filters] as const,
    featured: ['products', 'featured'] as const,
    detail: (slug: string) => ['products', 'detail', slug] as const,
    search: (q: string) => ['products', 'search', q] as const,
    relatedTo: (productId: number) => ['products', 'related', productId] as const,
  },
  categories: {
    all: ['categories'] as const,
    detail: (slug: string) => ['categories', slug] as const,
  },
  cart: ['cart'] as const,
  orders: {
    all: ['orders'] as const,
    list: (page: number) => ['orders', 'list', page] as const,
    detail: (id: number) => ['orders', 'detail', id] as const,
  },
  user: {
    me: ['user', 'me'] as const,
    addresses: ['user', 'addresses'] as const,
  },
  reviews: {
    forProduct: (productId: number) => ['reviews', 'product', productId] as const,
    mine: ['reviews', 'me'] as const,
  },
  wishlist: ['wishlist'] as const,
  admin: {
    dashboard: ['admin', 'dashboard'] as const,
    orders: (page: number, status?: string) =>
      ['admin', 'orders', page, status ?? 'all'] as const,
    lowStock: ['admin', 'lowStock'] as const,
    users: (q?: string, role?: string, page?: number) =>
      ['admin', 'users', { q: q ?? '', role: role ?? 'all', page: page ?? 1 }] as const,
    userAddresses: (userId: number) => ['admin', 'users', userId, 'addresses'] as const,
  },
} as const;
